import { toUrlRid } from '../../utils/tenantPath';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '../../routing';
import { commandCenterApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/Icon';
import ListRow from '../../components/ListRow';
import { useIsMobile } from '../../utils/useIsMobile';
import { RestaurantModal } from './modals';
import CustomerSettingsForm from './CustomerSettingsForm';
import { InvitePill, LoadState, RoleBadge, formatDate, openInApp, useRefetchOnFocus } from './parts';

// כרטיס לקוח — everything ops needs about one customer, with ZERO tenant
// switching. The console never selects a restaurant; anything that genuinely
// needs one opens the customer app in a new tab with the restaurant in the
// URL, via parts.jsx's shared openInApp.

export default function CustomerCardPage() {
  const { restaurantId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  // The open form registers its guarded close here, so the header button and
  // the form's own ביטול take the SAME exit — one that asks before discarding.
  const settingsCloseRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { refreshRestaurants } = useAuth();

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commandCenterApi.getCustomer(restaurantId);
      setData(r.data);
    } catch {
      setError(true);
      setData(null);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  useEffect(() => {
    if (data?.name) document.title = `${data.name} · Command Center`;
  }, [data]);

  // Arriving from a supplier search result: /customers/:id#supplier-:id.
  useEffect(() => {
    if (!data || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView({ block: 'center' });
  }, [data]);

  const brandNew = data && data.user_count === 0 && data.supplier_count === 0;

  return (
    <>
      <div className="cc-crumb">
        <Link to="/command-center/customers">לקוחות</Link>
        <Icon name="chevron-inline" size={13} className="cc-muted" />
        <span>{data?.name || '…'}</span>
      </div>

      <LoadState loading={!data && !error} error={error} onRetry={load} empty={false}>
        {data && (
          <>
            <div className="cc-card">
              <div className="cc-card-head" style={{ marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{data.name} <span className="cc-muted">· מספר לקוח {toUrlRid(data.id)}</span></div>
                  <div className="cc-muted" style={{ fontSize: '0.82rem', marginTop: 4, display: 'flex', gap: 10 }}>
                    <span>
                      {data.business_id
                        ? `ח.פ. ${data.business_id}`
                        : <span style={{ color: '#b91c1c' }}>חסר ח.פ.</span>}
                    </span>
                    {data.network_name && <span>{data.network_name}</span>}
                    <span>נוצרה {formatDate(data.created_at)}</span>
                  </div>
                </div>
                <div className="cc-spacer" />
                <button className="btn btn-sm btn-secondary" onClick={() => openInApp('/suppliers', data.id)}>
                  <Icon name="external" size={14} /> פתח באפליקציה
                </button>
                <button className="btn btn-sm btn-primary" onClick={() => setEditing(true)}>
                  <Icon name="edit" size={14} /> עריכה
                </button>
              </div>
            </div>

            {brandNew && (
              // A brand-new restaurant gets a real checklist instead of three
              // empty regions — the data is already fetched, so it costs nothing.
              <div className="cc-notice" style={{ marginBottom: 12 }}>
                <Icon name="sparkle" size={16} />
                <span>
                  לקוח חדש. כדאי להשלים: {!data.config.has_business_id && 'הוספת ח.פ. · '}
                  הוספת משתמש{!data.config.has_dokka && ' · הגדרת דוקה'}.
                </span>
              </div>
            )}

            <div className="cc-card">
              <div className="cc-card-head">
                <h2 className="cc-card-title">הגדרות</h2>
                <div className="cc-spacer" />
                {/* Editable HERE. This used to be a read-only mirror that sent
                    you to the app — but a console that shows a wrong Dokka
                    address and can't fix it just costs a tenant switch to do
                    the obvious thing. Both writers go through
                    lib/restaurantSettings and both pass a version token, so a
                    simultaneous save from /settings is rejected, not lost. */}
                <button className="btn btn-sm btn-secondary" onClick={() => openInApp('/settings', data.id)}>
                  <Icon name="external" size={14} /> באפליקציה
                </button>
                <button
                  className={`btn btn-sm ${editingSettings ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={() => {
                    if (editingSettings) (settingsCloseRef.current || (() => setEditingSettings(false)))();
                    else setEditingSettings(true);
                  }}
                >
                  <Icon name={editingSettings ? 'close' : 'edit'} size={14} />
                  {editingSettings ? ' סגור' : ' ערוך הגדרות'}
                </button>
              </div>
              {/* Expands in place, under the summary — the users and suppliers
                  you were looking at stay on screen, which a modal would cover. */}
              {editingSettings ? (
                <CustomerSettingsForm
                  customer={data}
                  closeRef={settingsCloseRef}
                  onSaved={() => { setEditingSettings(false); load(); }}
                  onCancel={() => setEditingSettings(false)}
                />
              ) : (
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.87rem' }}>
                  <span>מע״מ <b className="cc-num">{data.vat_rate ?? 18}%</b></span>
                  <span>מעקב תשלומים: <b>{data.config.payment_tracking ? 'פעיל' : 'כבוי'}</b></span>
                  <span>
                    דוקה:{' '}
                    {data.config.has_dokka
                      ? <b className="cc-mono">{data.dokka_email}</b>
                      : <span className="cc-muted">לא מוגדר</span>}
                  </span>
                  {data.dokka_forward_types?.length > 0 && (
                    <span className="cc-muted">סוגי מסמכים: {data.dokka_forward_types.join(', ')}</span>
                  )}
                </div>
              )}
            </div>

            <div className="cc-card">
              <div className="cc-card-head">
                <h2 className="cc-card-title">משתמשים · {data.users?.length || 0}</h2>
              </div>
              {data.users?.length ? (
                isMobile ? (
                  // A five-column table at 375px forced a sideways scroll and
                  // broke an email mid-word ("ops@peak-" / "tech.co.il").
                  <div className="m-list">
                    {data.users.map(u => (
                      <ListRow
                        key={u.id}
                        primary={<span dir="ltr">{u.email}</span>}
                        meta={
                          <>
                            <RoleBadge role={u.role} />
                            {u.role === 'admin'
                              ? <span>כל המסעדות</span>
                              : <InvitePill invite={u.invite} />}
                          </>
                        }
                        onClick={() => navigate(`/command-center/people/${u.id}`)}
                      />
                    ))}
                  </div>
                ) : (
                <div className="table-wrapper">
                  <table>
                    <tbody>
                      {data.users.map(u => (
                        <tr key={u.id} className="cc-row-link"
                          onClick={() => navigate(`/command-center/people/${u.id}`)}>
                          <td style={{ fontWeight: 500 }}>{u.email}</td>
                          <td><RoleBadge role={u.role} /></td>
                          <td>
                            {u.role === 'admin'
                              ? <span className="cc-muted">כל המסעדות</span>
                              : <InvitePill invite={u.invite} />}
                          </td>
                          <td className="cc-muted cc-num">{formatDate(u.created_at)}</td>
                          <td className="cc-go"><Icon name="chevron-inline" size={14} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )
              ) : (
                <div className="cc-empty">אף אחד לא משויך ללקוח הזה עדיין</div>
              )}
            </div>

            <div className="cc-card">
              <div className="cc-card-head">
                <h2 className="cc-card-title">ספקים · {data.supplier_count}</h2>
                <span className="cc-muted" style={{ fontSize: '0.8rem' }}>פעילים לאחרונה</span>
              </div>
              {data.suppliers?.length ? (
                isMobile ? (
                  <div className="m-list">
                    {data.suppliers.map(s => (
                      <ListRow
                        key={s.id}
                        primary={s.name}
                        meta={<><span>{s.status}</span><span>·</span><span>{formatDate(s.last_activity)}</span></>}
                        variant="plain"
                        onClick={() => openInApp(`/supplier/${s.id}`, data.id)}
                      />
                    ))}
                  </div>
                ) : (
                <div className="table-wrapper">
                  <table>
                    <tbody>
                      {data.suppliers.map(s => (
                        <tr key={s.id} id={`supplier-${s.id}`}>
                          <td>{s.name}</td>
                          <td className="cc-muted">{s.status}</td>
                          <td className="cc-muted cc-num">{formatDate(s.last_activity)}</td>
                          <td style={{ width: 130 }}>
                            <button className="btn btn-sm btn-secondary"
                              onClick={() => openInApp(`/supplier/${s.id}`, data.id)}>
                              <Icon name="external" size={13} /> פתח
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )
              ) : (
                <div className="cc-empty">אין ספקים עדיין</div>
              )}
            </div>
          </>
        )}
      </LoadState>


      {editing && data && (
        <RestaurantModal
          restaurant={data}
          onSave={() => { setEditing(false); load(); refreshRestaurants(); }}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}
